import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { FavoriteAddToCartButton } from '~/components/FavoriteAddToCartButton';
import { CDPrice as Price } from '~/components/Price/CDPrice';
import { useProductCard } from '~/components/ProductCard/hooks/useProductCard';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { cartStore, catalogStore, mainStore } from '~/stores';
import type { Product } from '~/stores/CategoriesStore';
import { isProductBundle } from '~/types/Product';
import {
  calcDiscountPercentage,
  convertPenceToPounds,
} from '~/utils/formaters';
import isNumber from '~/utils/isNumber';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
} from '~/utils/multibuy';

import styles from './FavProductCard.module.scss';

const FavProductCard = ({
  product,
  observerKey,
}: {
  product: Product;
  observerKey: string;
}) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();

  const { currentItem, limit } = useProductCard(product, product.offers[0]);
  const productId = product.offers[0]?.productId ?? product.id ?? 0;
  const alreadySubscribed =
    productId &&
    currentItem.sku &&
    catalogStore.notifyMeStore.notifyMeSKUMap[productId]?.includes(
      currentItem.sku,
    );

  const isBundle = isProductBundle(currentItem);
  const cartItemsCount = cartStore.getCartItemCountById(currentItem);
  const isMultiBuy = Boolean(
    !isBundle && currentItem.multibuyPriceLevels?.length,
  );
  const applicableLevel = isMultiBuy
    ? getApplicableMultibuyLevel(
        currentItem.multibuyPriceLevels,
        cartItemsCount,
      )
    : null;
  const lowestLevel = isMultiBuy
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const isMultiBuyActive = Boolean(isMultiBuy && applicableLevel);

  const priceData = useMemo(() => {
    // Only show multibuy price if multibuy is active
    if (isMultiBuyActive && applicableLevel) {
      const multibuyPrice = convertPenceToPounds(applicableLevel.unitPrice);
      const basePrice = currentItem.discountPrice || currentItem.price || 0;
      const basePriceFormatted = currentItem.discountPrice
        ? currentItem.priceFormatted
        : undefined;

      // Calculate discount percentage for multibuy
      let discountPercent = currentItem.discountPercentage;
      if (
        isNumber(basePrice) &&
        basePrice > 0 &&
        applicableLevel.unitPrice > 0
      ) {
        const percent = calcDiscountPercentage(
          basePrice,
          applicableLevel.unitPrice,
        );
        discountPercent = `-${percent}%`;
      }

      return {
        price: multibuyPrice,
        discount: basePriceFormatted,
        discountPercent,
      };
    }

    // If multibuy exists but is not active, show regular price with multibuy info in discountPercent
    if (isMultiBuy && lowestLevel && !isMultiBuyActive) {
      const basePrice = currentItem.discountPrice || currentItem.price || 0;

      let discountPercent = currentItem.discountPercentage;
      if (isNumber(basePrice) && basePrice > 0 && lowestLevel.unitPrice > 0) {
        const percent = calcDiscountPercentage(
          basePrice,
          lowestLevel.unitPrice,
        );
        const countLeft = Math.max(0, lowestLevel.quantity - cartItemsCount);
        discountPercent =
          countLeft > 0
            ? t('phrases:multibuyFrom', {
                count: countLeft,
                percent: `${percent}%`,
              })
            : `-${percent}%`;
      }

      return {
        price: currentItem.discountPrice
          ? currentItem.discountPriceFormatted
          : currentItem.priceFormatted,
        discount: currentItem.discountPrice
          ? currentItem.priceFormatted
          : undefined,
        discountPercent,
      };
    }

    // Regular price without multibuy
    return {
      price: currentItem.discountPrice
        ? currentItem.discountPriceFormatted
        : currentItem.priceFormatted,
      discount: currentItem.discountPrice
        ? currentItem.priceFormatted
        : undefined,
      discountPercent: currentItem.discountPercentage,
    };
  }, [
    isMultiBuyActive,
    isMultiBuy,
    applicableLevel,
    lowestLevel,
    currentItem,
    cartItemsCount,
    t,
  ]);

  const onClose = () => {
    mainStore.setHeaderDropdownOpen(observerKey, false);
  };

  const handleClick = () => {
    const analyticFn = handleClickProduct(product);

    onClose();
    analyticFn();
  };

  return (
    <Link
      to={`/p/${product.slug}`}
      className={styles.root}
      onClick={handleClick}
    >
      <div className={styles.imgWr}>
        <CloudflareResponseImage
          src={product.previewImageThumb}
          srcParams={{ width: 80 }}
          srcSetParams={defaultSrcSetParams}
          alt={product.name}
          loading="lazy"
        />
      </div>
      <div className={styles.content}>
        <div className={styles.productInfo}>
          <h3>{product.name}</h3>
          {limit > 0 ? (
            <Price
              price={priceData.price}
              discount={priceData.discount}
              discountPercent={priceData.discountPercent}
              classes={{
                root: styles.price,
                classNameCurrentPrice: styles.currentPrice,
                classNameOldPrice: styles.oldPrice,
              }}
            />
          ) : (
            <p className={styles.notifyLabel}>
              {alreadySubscribed
                ? t('phrases:weWillNotify')
                : t('phrases:subscribeOnNotify')}
            </p>
          )}
        </div>

        <FavoriteAddToCartButton product={product} onClose={onClose} />
      </div>
    </Link>
  );
};

export default observer(FavProductCard);
